import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./navbar.css";
import { CONTACT_EMAIL, HOME_SECTION_IDS, NAV_ITEMS } from "../../../Constants/Home";
import { useActiveSection } from "../../../Hooks/useActiveSection";
import { useTranslation } from "../../../Hooks/useTranslation";
import LanguageSwitch from "../../Elements/LanguageSwitch/LanguageSwitch";

const LOGO = "/assets/CASVI_Brand_Kit/PNG/CASVI_logo_transparent_2400px.png";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const activeSection = useActiveSection(HOME_SECTION_IDS);
  const active = pathname.startsWith("/apps") ? "apps" : pathname === "/" ? activeSection : null;
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Con el menú abierto: bloquea el scroll de atrás, cierra con Escape y mueve el foco
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    firstLinkRef.current?.focus({ preventScroll: true });
    const toggle = toggleRef.current;
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKeyDown);
      toggle?.focus({ preventScroll: true });
    };
  }, [open]);

  // Si giran el teléfono o agrandan la ventana a escritorio, cerramos el menú
  useEffect(() => {
    const media = window.matchMedia?.("(min-width: 761px)");
    if (!media?.addEventListener) return;
    const onChange = (e) => e.matches && setOpen(false);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return (
    <>
      <header className={`navbar ${scrolled ? "scrolled" : ""} ${open ? "menu-open" : ""}`}>
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo" onClick={closeMenu}>
            <img src={LOGO} alt="Casvi Software" />
          </Link>
          <nav className="navbar-links" aria-label="Principal">
            {NAV_ITEMS.map((item) => (
              <Link key={item.id} to={item.to} className={active === item.id ? "active" : ""}>
                {t.nav.links[item.id]}
              </Link>
            ))}
            <Link to="/#contacto" className="navbar-cta">
              {t.nav.cta}
            </Link>
            <LanguageSwitch classNames="navbar-language" />
          </nav>
          <div className="navbar-mobile-actions">
            <LanguageSwitch />
            <button
              ref={toggleRef}
              className={`navbar-toggle ${open ? "open" : ""}`}
              aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((prev) => !prev)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* Cortina: cubre todo lo de atrás y cierra el menú al tocarla */}
      <div
        className={`menu-curtain ${open ? "open" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <aside
        id="mobile-menu"
        className={`mobile-menu ${open ? "open" : ""}`}
        aria-label={t.nav.menu}
        aria-hidden={!open}
        inert={!open}
      >
        <nav className="mobile-menu-links">
          {NAV_ITEMS.map((item, index) => (
            <Link
              key={item.id}
              ref={index === 0 ? firstLinkRef : undefined}
              to={item.to}
              className={active === item.id ? "active" : ""}
              style={{ "--i": index }}
              onClick={closeMenu}
            >
              <span className="mobile-menu-index">0{index + 1}</span>
              <span className="mobile-menu-label">{t.nav.links[item.id]}</span>
              <span className="mobile-menu-arrow" aria-hidden="true">→</span>
            </Link>
          ))}
        </nav>

        <div className="mobile-menu-footer" style={{ "--i": NAV_ITEMS.length }}>
          <Link to="/#contacto" className="mobile-menu-cta" onClick={closeMenu}>
            {t.nav.mobileCta}
          </Link>
          <a href={`mailto:${CONTACT_EMAIL}`} className="mobile-menu-email">
            {CONTACT_EMAIL}
          </a>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
